import type { EnMode } from '../components/watch/Transcript';

/**
 * Two decisions Watch makes on every gloss, extracted so they can be held still by a test.
 * Watch.tsx itself is 2,000 lines of player wiring and is not unit-testable under node.
 */

/**
 * D9: opening the gloss sheet pauses playback on mobile, and the sheet carries ▶ Resume.
 *
 * Desktop never paused — the gloss docks beside the player, so there is nothing to lose by
 * reading it. M5 gives that same choice to phone users through Me → "keep playing while I read
 * glosses": when it is on, mobile behaves like desktop.
 *
 * Note it is `desktop`, not `columns`: a phone in landscape gets the two-panel layout but its
 * gloss is still a sheet over the player, so it keeps the pause.
 */
export function shouldPauseForSheet(
  desktop: boolean,
  keepPlaying: boolean,
  playing: boolean,
): boolean {
  return !desktop && !keepPlaying && playing;
}

/**
 * Which EN mode a transcript opens in.
 *
 * The learner's saved preference is the answer — except for one transcript-specific case: with
 * no translations at all, "tap to reveal" would put "translation pending" under every line, so
 * EN starts off until a translate job lands. That is a fact about this video, not a change to
 * what the learner asked for, so the preference is restored the moment translations arrive.
 *
 * `touched` is the in-session cycle from the transport strip: once the learner has pressed EN
 * on this video, nothing may move it under them — not even a job finishing.
 */
export function initialEnMode(
  preference: EnMode | undefined,
  translationStatus: string | undefined,
  touched: boolean,
  current: EnMode,
): EnMode {
  if (touched) return current;
  if (translationStatus === 'none') return 'hide';
  return preference ?? 'reveal';
}
